import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Zero-Day Fuzzing (level 75) — the Deduction rung.
//
// Nothing here polls and nothing runs on a clock: the board only changes when the player fuzzes. So there
// is no `busy` in the props below either — a re-entrancy guard is a ref, and the buttons never flicker.
//
// WHAT THIS VIEW DOES NOT SHOW, and it is the whole of its discipline: there is no list of still-possible
// required sets and no count of them. Triangulation does show its surviving candidates, because there the
// board is a map and withholding them would turn the rung into a memory test. Here narrowing the
// hypotheses IS the deduction, so a view that narrowed them would be the solver. What it shows is the
// evidence — which conditions each input broke, and what the crash report said back.
//
// WHAT IT DOES SHOW, after a playtest read it exactly backwards. The first version labelled the two
// columns HOLD and REQUIRED, adjacent and alike, and a player who broke five conditions, saw a green
// CRASHED and then clicked the neighbouring button on those same five rows was told they had none of them
// right — because a crash proves the opposite: the guard cleared everything, so nothing that was broken
// mattered. Three faults, all mine. The verbs were ambiguous (you HOLD a condition back and you name it
// REQUIRED; both are things you do to a row); a green CRASHED reads as success rather than as five
// exonerations; and the report was never TRANSLATED where it is read, only in a paragraph of the brief.
//
// So: two verbs that cannot be confused — you BREAK a condition to test it, you BLAME one as your answer —
// the answer column moved to the far side of the row behind a divider, every report restated in terms of
// the set that was broken, and a confirmation before the single declaration is spent.
//
// What is deliberately NOT added is a check that the blame contradicts the evidence. That is half a
// solver: a player could toggle until the warning cleared. Restating a report is applying the published
// rule to the player's own action; deciding which sets survive it is the game.

interface CondV {
  index: number;
  text: string;
  field: string;
  bucket: number;
}

interface RunV {
  satisfy: boolean[];
  reached: number;
  line: number;
  crashed: boolean;
}

interface Challenge {
  started: boolean;
  requiredCount: number;
  runBudget: number;
  runsLeft: number;
  buckets: number;
  timeLimitSec: number;
  readingAllowanceSec: number;
  conditions: CondV[];
  bucketLabels: string[];
  runs: RunV[];
}

export default function ZeroDayFuzzingView({
  challenge, onAct, onSubmit, setPendingSubmission,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [satisfy, setSatisfy] = useState<boolean[]>([]);
  const [declared, setDeclared] = useState<number[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const sendingRef = useRef(false);

  const conds = useMemo(() => live?.conditions ?? [], [live]);
  const runs = useMemo(() => live?.runs ?? [], [live]);
  const started = live?.started ?? false;
  const need = live?.requiredCount ?? 0;

  useEffect(() => {
    setLive(challenge ?? null);
    setSatisfy(challenge?.conditions?.map(() => true) ?? []);
    setDeclared([]);
    setNote(null);
    setConfirming(false);
    sendingRef.current = false;
  }, [challenge]);

  useEffect(() => { setPendingSubmission({ set: declared }); }, [declared, setPendingSubmission]);

  const send = useCallback(async (action: unknown) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setNote(null);
    try {
      const next = await onAct(action);
      if (next) {
        setLive(next);
        if (next.conditions?.length && satisfy.length !== next.conditions.length)
          setSatisfy(next.conditions.map(() => true));
      }
    } finally {
      sendingRef.current = false;
    }
  }, [onAct, satisfy.length]);

  const mono: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };
  const small: React.CSSProperties = { ...mono, fontSize: '0.72rem' };

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            The disassembly shows the guard's individual conditions, grouped by which field of the input
            each one tests. What it does not show is <strong>which of them the vulnerable branch
            requires</strong> — only how many. They all have to hold together.
          </p>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            A run is an input you build: pick conditions to <strong>break</strong> and your input satisfies
            all the rest. The guard <strong>short-circuits</strong> — it works down its conditions in the
            order they appear here and stops at the first one your input fails — so the crash report tells
            you <strong>how far it got</strong>, and which field the condition it died on was testing.
          </p>
          <div style={{
            border: '1px solid #b45309', borderRadius: 4, padding: '0.5rem 0.7rem',
            margin: '0 0 0.6rem', background: '#1a1206',
          }}>
            <p style={{ ...mono, color: '#fbbf24', margin: 0, lineHeight: 1.6 }}>
              Read this one twice, because it runs the wrong way round.{' '}
              <strong>If it still crashes, everything you broke was irrelevant.</strong> A crash means the
              guard got through all of its conditions, so none of the ones you broke were among them — the
              run has <em>exonerated</em> them, not found them. What convicts a condition is a run that
              <strong> stops early</strong>.
            </p>
          </div>
          <p style={{ ...mono, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            The short-circuit is also what makes an input worth designing. Break five conditions and you
            hear about exactly one of them — the earliest the guard wanted — so a scattergun input wastes
            the run. The informative input breaks <em>a carefully chosen few</em>.
          </p>
          <p style={{ ...mono, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            You get <strong>{live?.runBudget ?? 5} runs</strong> and{' '}
            <strong>one</strong> chance to name the branch. Every board can be pinned down inside that budget —
            that is checked before it is handed to you — but only by inputs that are actually worth firing.
            {(live?.buckets ?? 1) > 1
              ? ` Your Cracker reads the fault frame well enough to split the report across ${live?.buckets} groups of fields, so it names where the guard died.`
              : ' Your Cracker recovers only how far the guard got, never which field — the report is a single figure.'}
          </p>
          <p style={{ ...mono, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            The disassembly arrives when you load the report. The clock is already running — about{' '}
            {live?.readingAllowanceSec ?? 35}s of it is set aside for this.
          </p>
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Load the crash report
          </button>
        </div>
      </div>
    );
  }

  const runsLeft = live?.runsLeft ?? 0;
  const held = conds.filter((_, i) => !satisfy[i]).map(c => c.index);
  const fields = [...new Set(conds.map(c => c.field))];

  const toggleDeclared = (i: number) => {
    setConfirming(false);
    setDeclared(d => d.includes(i) ? d.filter(x => x !== i) : d.length >= need ? d : [...d, i]);
  };

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', whiteSpace: 'nowrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.55rem', background: '#0d1117',
      }}>
        <span style={{ ...mono, minWidth: '9.5rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>RUNS LEFT </span>
          <strong style={{ color: runsLeft > 1 ? '#cbd5e1' : runsLeft > 0 ? '#fbbf24' : '#f87171' }}>
            {runsLeft}
          </strong>
          <span style={{ color: '#4b5563' }}> / {live?.runBudget ?? 0}</span>
        </span>
        <span style={{ ...mono, minWidth: '11rem', color: 'var(--text-muted)', fontSize: '0.74rem' }}>
          the branch requires <strong style={{ color: '#e2e8f0' }}>{need}</strong> of these
        </span>
        <span style={{
          ...mono, minWidth: '9rem', fontSize: '0.74rem',
          color: declared.length === need ? '#4ade80' : 'var(--text-muted)',
        }}>
          named <strong>{declared.length}</strong> / {need}
        </span>
        <span style={{ ...small, minWidth: '11rem', color: held.length === 0 ? '#4b5563' : '#fbbf24' }}>
          next input breaks {held.length === 0 ? 'nothing' : held.join(', ')}
        </span>
      </div>

      <div style={{ overflowX: 'auto', marginBottom: '0.55rem' }}>
        <div style={{ minWidth: 520 }}>
          <div style={{
            display: 'flex', gap: '0.4rem', padding: '0.2rem 0.55rem', whiteSpace: 'nowrap',
            ...small, color: '#4b5563', borderBottom: '1px solid #1c2029',
          }}>
            <span style={{ minWidth: '2.2rem' }}>#</span>
            <span style={{ minWidth: '5.2rem', color: '#b45309' }}>BREAK?</span>
            <span style={{ minWidth: '5.5rem' }}>field</span>
            <span style={{ minWidth: '10rem' }}>condition</span>
            <span style={{ minWidth: '7rem' }}>reports in</span>
            <span style={{ minWidth: '0.9rem' }}>|</span>
            <span style={{ minWidth: '5.6rem', color: 'var(--primary-color)' }}>BLAME?</span>
          </div>

          {fields.map(field => (
            <div key={field}>
              {conds.map((c, i) => {
                if (c.field !== field) return null;
                const on = satisfy[i] ?? true;
                const named = declared.includes(c.index);
                return (
                  <div key={c.index} style={{
                    display: 'flex', gap: '0.4rem', alignItems: 'center', whiteSpace: 'nowrap',
                    padding: '0.15rem 0.55rem',
                    background: named ? '#131c2b' : 'transparent',
                    borderLeft: `2px solid ${named ? 'var(--primary-color)' : 'transparent'}`,
                  }}>
                    <span style={{ ...small, minWidth: '2.2rem', color: '#4b5563' }}>{c.index}</span>
                    <button
                      onClick={() => setSatisfy(a => a.map((x, j) => (j === i ? !x : x)))}
                      style={{
                        ...small, minWidth: '5.2rem', padding: '0.08rem 0.3rem',
                        border: `1px solid ${on ? '#2b3444' : '#b45309'}`,
                        background: on ? 'transparent' : '#241a06',
                        color: on ? '#6b7280' : '#fbbf24', borderRadius: 3, cursor: 'pointer',
                      }}
                      title={on ? 'The next input satisfies it' : 'The next input breaks it'}
                    >
                      {on ? 'intact' : 'BROKEN'}
                    </button>
                    <span style={{ ...small, minWidth: '5.5rem', color: 'var(--text-muted)' }}>{c.field}</span>
                    <span style={{ ...mono, minWidth: '10rem', color: '#cbd5e1' }}>{c.text}</span>
                    <span style={{ ...small, minWidth: '7rem', color: '#6b7280' }}>
                      {(live?.bucketLabels?.[c.bucket] ?? '').split(' + ').length > 1
                        ? `line ${c.bucket + 1}`
                        : live?.bucketLabels?.[c.bucket] ?? ''}
                    </span>
                    {/* The answer lives on the far side of a divider, and under a different verb. Two
                        adjacent lookalike buttons is what got a playtest naming the five it had just
                        proved innocent. */}
                    <span style={{ ...small, minWidth: '0.9rem', color: '#232833' }}>|</span>
                    <button
                      onClick={() => toggleDeclared(c.index)}
                      style={{
                        ...small, minWidth: '5.6rem', padding: '0.08rem 0.3rem',
                        border: `1px solid ${named ? 'var(--primary-color)' : '#2b3444'}`,
                        background: named ? '#1d283a' : 'transparent',
                        color: named ? '#93c5fd' : '#4b5563', borderRadius: 3, cursor: 'pointer',
                      }}
                      title="Your answer: claim the branch requires this one"
                    >
                      {named ? 'BLAMED' : 'blame'}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.45rem' }}>
        <button
          className="primary"
          onClick={() => {
            if (runsLeft <= 0) { setNote('No runs left — name the branch.'); return; }
            void send({ kind: 'run', satisfy });
          }}
          style={{ fontSize: '0.85rem', padding: '0.3rem 0.8rem' }}
        >
          Fuzz it
        </button>
        <button
          onClick={() => setSatisfy(conds.map(() => true))}
          style={{ ...small, padding: '0.2rem 0.5rem' }}
        >
          satisfy everything
        </button>
        <button
          onClick={() => setSatisfy(a => a.map(x => !x))}
          style={{ ...small, padding: '0.2rem 0.5rem' }}
        >
          invert
        </button>
        {fields.map(field => (
          <button
            key={field}
            onClick={() => setSatisfy(a => a.map((x, j) => conds[j].field === field ? !x : x))}
            style={{ ...small, padding: '0.2rem 0.45rem', color: '#9aa4b2' }}
            title={`Flip every condition on ${field}`}
          >
            {field}
          </button>
        ))}
      </div>

      {/* The evidence, and nothing derived from it. A fixed slot, so a new report never moves the buttons. */}
      <div style={{
        minHeight: '6.6rem', marginBottom: '0.5rem', overflow: 'hidden',
        border: '1px solid #1c2029', borderRadius: 4, padding: '0.3rem 0.55rem', background: '#0a0d12',
      }}>
        {runs.length === 0 && (
          <span style={{ ...small, color: '#4b5563' }}>
            no runs yet — a run that crashes clears the conditions you broke; one that stops early convicts
            one of them
          </span>
        )}
        {runs.map((r, i) => {
          const back = conds.filter((_, j) => !r.satisfy[j]).map(c => c.index);
          const where = live?.bucketLabels?.[r.line] ?? '?';
          const many = (live?.buckets ?? 1) > 1;
          return (
            <div key={i} style={{ ...small, whiteSpace: 'nowrap', marginBottom: '0.1rem' }}>
              <span style={{ color: '#4b5563' }}>#{i + 1} </span>
              <span style={{ color: '#fbbf24' }}>
                broke {back.length === 0 ? 'nothing' : `[${back.join(', ')}]`}
              </span>
              <span style={{ color: '#4b5563' }}>{' → '}</span>
              {r.crashed ? (
                <span style={{ color: '#4ade80' }}>
                  <strong>CRASHED</strong>
                  <span style={{ color: '#6b7280' }}>
                    {back.length === 0
                      ? ' · nothing was broken, so nothing was learnt'
                      : ` · so the guard needs NONE of [${back.join(', ')}]`}
                  </span>
                </span>
              ) : (
                <span style={{ color: '#e2e8f0' }}>
                  cleared <strong>{r.reached}</strong> of {need}
                  <span style={{ color: '#6b7280' }}>
                    {' · so the guard\u2019s condition #'}{r.reached + 1}
                    {' is one of ['}{back.join(', ')}{']'}
                    {many ? `, and it is in ${where}` : ''}
                  </span>
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ minHeight: '1.3rem', marginBottom: '0.2rem', overflow: 'hidden' }}>
        {note && <span style={{ ...small, color: '#fbbf24' }}>{note}</span>}
      </div>

      {/* One declaration, so it gets a confirmation that spells out what is being claimed. The playtest
          that lost an attempt to a misreading would have caught it here. */}
      <div style={{ minHeight: '2.6rem', marginBottom: '0.3rem', overflow: 'hidden' }}>
        {confirming && (
          <div style={{
            border: '1px solid var(--primary-color)', borderRadius: 4, padding: '0.35rem 0.6rem',
            background: '#131c2b',
          }}>
            <div style={{ ...small, color: '#93c5fd' }}>
              You are claiming the branch requires exactly these {need}, and there is no second attempt:
            </div>
            <div style={{ ...small, color: '#e2e8f0', whiteSpace: 'normal' }}>
              {declared.map(i => conds.find(c => c.index === i)?.text ?? `#${i}`).join('  ·  ')}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => {
            if (declared.length !== need) {
              setNote(`Blame exactly ${need} condition${need === 1 ? '' : 's'} — the disassembly says how many.`);
              return;
            }
            if (!confirming) { setConfirming(true); return; }
            void send({ kind: 'declare', set: declared });
          }}
          style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
          title="One declaration only"
        >
          {confirming ? 'Confirm — this is final' : 'Name the branch'}
        </button>
        <button
          onClick={() => { setConfirming(false); setDeclared([]); }}
          style={{ ...small, padding: '0.25rem 0.6rem' }}
        >
          clear the blame
        </button>
        <button
          onClick={() => onSubmit({ set: declared })}
          style={{ ...small, padding: '0.25rem 0.6rem' }}
          title="Stop here and let whatever is named stand"
        >
          Give up
        </button>
        <span style={{ ...small, color: '#4b5563' }}>
          the guard evaluates in the order listed above
        </span>
      </div>
    </div>
  );
}
